import { Suspense, useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/browser";
import { V2Divider, V2Heading, V2SearchField } from "@stacklet/ui";
import { useMutation, useRelayEnvironment } from "react-relay";

import {
  UserBrowser,
  type GroupMembership,
  type TransferUser,
} from "app/components/rbac/UserBrowser";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { useAlertContext } from "app/contexts";
import { UpdateUserGroupMembersMutation } from "app/mutations";
import { invalidateFieldRecords } from "app/utils/relay";

import type { UpdateUserGroupMembersMutation as UpdateUserGroupMembersMutationType } from "app/mutations/__generated__/UpdateUserGroupMembersMutation.graphql";

/** Props for the SectionHeader component */
interface SectionHeaderProps {
  /** Label for the list below, e.g. "Members" */
  children: string;
}

/** The banded label that introduces each of the two lists. */
function SectionHeader({ children }: SectionHeaderProps) {
  return (
    <div className="mb-sm rounded-sm bg-background-secondary px-md py-sm text-label-medium text-text-primary">
      {children}
    </div>
  );
}

/** Props for the GroupMembershipTransferList component */
interface Props {
  /** Stable uuid of the group whose membership is being edited */
  groupUuid: string;
  /** Name of the group, which partitions users into members and non-members */
  groupName: string;
  /** Whether the viewer may change this group's membership */
  canEditMembership: boolean;
}

/**
 * Two-sided transfer list for a group's direct membership: the group's members
 * above, everyone else below, with a `−` on each member that removes them and a
 * `+` on each non-member that adds them.
 *
 * Both lists are paginated server-side and share the single search field at the
 * top, which filters them by username in step. Each transfer commits
 * immediately via `updateUserGroupMembers` — it is not staged with the rest of
 * the edit form — and on success both lists refetch from their first page so
 * the moved user lands on the other side.
 *
 * Only locally-managed groups can be edited this way: membership of a group
 * provisioned by an identity provider is owned by the login sync, and the API
 * rejects manual changes to it.
 */
export function GroupMembershipTransferList({
  groupUuid,
  groupName,
  canEditMembership,
}: Props) {
  const { alertDispatch } = useAlertContext();
  // The environment the mutation commits through, so the records enumerated
  // for invalidation are the ones its updater is writing to.
  const relayEnvironment = useRelayEnvironment();
  // `search` is the live input; `appliedSearch` is the debounced term handed to
  // both lists, so a keystroke refetches them at most once per debounce window.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Bumped after every successful change so both lists reload: the mutation
  // response carries only the group, so Relay can't know which user connections
  // it invalidated.
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const [updateMembers, isUpdating] =
    useMutation<UpdateUserGroupMembersMutationType>(
      UpdateUserGroupMembersMutation,
    );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setAppliedSearch(value),
      SEARCH_DEBOUNCE_MS,
    );
  };

  // Enter searches now rather than waiting out the debounce. Handling it is
  // also what stops the keypress submitting the surrounding edit form: the
  // search field only calls preventDefault on Enter when it has an onSubmit,
  // so without this, searching here would save the group and leave the page.
  const handleSearchSubmit = (value: string) => {
    clearTimeout(debounceRef.current);
    setAppliedSearch(value);
  };

  const transfer = (user: TransferUser, membership: GroupMembership) => {
    // A member moves out, a non-member moves in.
    const isRemoval = membership === "members";
    const action = isRemoval ? "removed from" : "added to";

    const fail = (message: string) => {
      Sentry.captureException(new Error(message));
      alertDispatch({
        message: `Failed to ${isRemoval ? "remove" : "add"} ${user.label}: ${message}`,
        severity: "error",
      });
    };

    updateMembers({
      variables: {
        input: {
          groupUUID: groupUuid,
          ...(isRemoval ? { remove: [user.uuid] } : { add: [user.uuid] }),
        },
      },
      updater(store) {
        // Relay runs this the moment the payload arrives, before onCompleted
        // gets to judge it. A rejected transfer still answers 200 with the
        // reason in `problems`, so check for one here too: invalidating after
        // a change that never landed would send every other view off to
        // refetch data that hasn't moved.
        const payload = store.getRootField("updateUserGroupMembers");
        if (!payload || payload.getLinkedRecords("problems")?.length) {
          return;
        }

        // The payload is just the group, so Relay can't tell which cached data
        // this change invalidated. Mark it stale by hand rather than patching
        // edges: the affected lists are filtered and cursor-paginated, so a
        // moved user belongs on a page the server never sent us — which also
        // rules out @appendNode/@deleteEdge, since neither list is a
        // `@connection` and the payload doesn't return the member.
        //
        // Only the `users(filterElement: …)` pages filtered by this group: the
        // two lists here and the browser on its details page. Pages listing
        // users on other terms hold the same rows in the same order after a
        // membership change, and their per-user group data hangs off the user
        // records invalidated below.
        invalidateFieldRecords(
          relayEnvironment,
          "users",
          "client:root",
          store,
          // Quoted, as the serialised arguments are, so a group named
          // "Engineering" doesn't match pages filtered by "Engineering
          // Management".
          JSON.stringify(groupName),
        );

        // The group itself, so its details page (and the members hanging off
        // its record) reload rather than showing the old membership.
        const groupId = payload.getLinkedRecord("userGroup")?.getValue("id");
        if (typeof groupId === "string") {
          store.get(groupId)?.invalidateRecord();
        }

        // The moved user, whose own details page lists the groups they belong
        // to. Their record is in whichever list they came from.
        store.get(user.id)?.invalidateRecord();
      },
      onCompleted(response, errors) {
        // Valid entries are still applied when others fail, but a single-user
        // change has nothing left to succeed, so treat any problem as failure.
        const problems = response.updateUserGroupMembers?.problems ?? [];
        if (errors?.length || problems.length) {
          fail(errors?.[0]?.message ?? problems[0]?.message ?? "Unknown error");
          return;
        }

        alertDispatch({
          message: `${user.label} ${action} the group`,
          severity: "success",
        });
        setRefreshToken((token) => token + 1);
      },
      onError(error) {
        fail(error.message);
      },
    });
  };

  const disabledReason = !canEditMembership
    ? "You do not have permission to change this group's membership"
    : "Applying the previous change…";

  /** Props shared by both halves of the transfer list */
  const sharedProps = {
    description: null,
    enableExport: false,
    isTransferDisabled: !canEditMembership || isUpdating,
    refreshToken,
    searchTerm: appliedSearch,
    title: null,
    transferDisabledReason: disabledReason,
  } as const;

  return (
    <div className="flex w-full flex-col">
      <V2Heading
        className="text-label-medium-strong text-text-primary"
        level={3}
      >
        Users
      </V2Heading>
      <V2Divider className="mt-xs" />
      <p className="mt-sm text-label-small text-text-secondary">
        Add users to this group, or remove them from it. Membership changes are
        saved as you make them.
      </p>

      <div className="mt-md rounded-sm bg-background-secondary px-md py-xs">
        <V2SearchField
          aria-label="Search users"
          className="w-full max-w-[20rem]"
          inputProps={{ placeholder: "Search..." }}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          value={search}
        />
      </div>

      <div className="mt-md">
        <SectionHeader>Members</SectionHeader>
        <Suspense fallback={null}>
          <UserBrowser
            {...sharedProps}
            onTransfer={(user) => transfer(user, "members")}
            scope={{ kind: "group", groupName, membership: "members" }}
            tableId="group-members-browser"
          />
        </Suspense>
      </div>

      <div className="mt-lg">
        <SectionHeader>Non-Members</SectionHeader>
        <Suspense fallback={null}>
          <UserBrowser
            {...sharedProps}
            onTransfer={(user) => transfer(user, "non-members")}
            scope={{ kind: "group", groupName, membership: "non-members" }}
            tableId="group-non-members-browser"
          />
        </Suspense>
      </div>
    </div>
  );
}
